import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { readFeatureFlagFromCookieHeader, readFeatureFlagFromDocumentCookie } from './read-feature-flag';

/**
 * Navbar redesign Phase 1 (navbar live-index proposal): switches every React-rendered header
 * (NavigationNavbar on non-SRP pages, DesktopFilterCategories row one on the SRP) between the
 * current chrome and the new "live index" header (fused search + location, a single account
 * pill, bars-only menu, and the row-two market-figures strip on non-SRP pages). On by default (the prerendered pages ship it
 * unconditionally); setting the cookie to "false" is the kill switch back to the classic header. Managed from `/backoffice/experiments`
 * (see feature-flag-registry.ts) on the shared cookie mechanism in read-feature-flag.ts. The
 * static prerendered twin (ResourceNavbar, web-static) and the SRP's filter tape/sub-row are
 * out of scope for this flag; see docs/knowledge-graph for the fuller picture once it lands.
 */
export const NAVBAR_LIVE_INDEX_COOKIE = 'dev_enable_navbar_live_index';

/** Exported for unit testing the cookie-parsing logic directly, without rendering the hook. */
export function readNavbarLiveIndexOptIn(): boolean {
  return readFeatureFlagFromDocumentCookie(NAVBAR_LIVE_INDEX_COOKIE, true);
}

/** SSR-side reader, takes a raw `Cookie` header string since `document` doesn't exist during SSR. */
export function readNavbarLiveIndexOptInFromCookieHeader(cookieHeader: string | null | undefined): boolean {
  return readFeatureFlagFromCookieHeader(cookieHeader, NAVBAR_LIVE_INDEX_COOKIE, true);
}

/**
 * Starts false to match SSR, then picks up the cookie post-hydrate, so there's no flash of the
 * new header for visitors who haven't opted in and no SSR/client markup mismatch.
 *
 * Phase 1 review fix 2.8: pass `initialValue` (read server-side from `pageContext.
 * navbarLiveIndexEnabled`, seeded in server/vike-handler.ts via
 * readNavbarLiveIndexOptInFromCookieHeader) so an opted-in visitor's FIRST client render already
 * shows the new header too, instead of starting false and flipping true a moment after hydration.
 * Omit it (or pass undefined) to keep the old false-then-cookie behaviour.
 */
export function useNavbarLiveIndexEnabled(initialValue?: boolean): boolean {
  return useFeatureFlag(NAVBAR_LIVE_INDEX_COOKIE, true, initialValue);
}
